import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { AREAS } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Booking() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", area: "", interest: "Package of 11", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/booking`, form);
      setDone(true);
      toast.success("Enquiry sent — I'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-section" className="bg-[#18181B] text-[#FAF7F2] py-24 sm:py-32 lg:py-40 grain">
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-14">
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <p className="eyebrow label text-[#C5A059] mb-6">Get in touch</p>
            <h2 className="font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">
              Reserve your first session.
            </h2>
            <p className="mt-6 text-[#FAF7F2]/70 text-lg leading-relaxed max-w-md">
              Share a few details and I'll personally reply to arrange a time that suits you. Serving Hampstead and the surrounding villages.
            </p>
            <div className="mt-10 flex flex-wrap gap-2" data-testid="booking-areas">
              {AREAS.map((a) => (
                <span key={a} className="label text-[0.6rem] text-[#FAF7F2]/70 border border-[#FAF7F2]/15 rounded-full px-3 py-1.5">{a}</span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                data-testid="booking-success"
                className="h-full min-h-[420px] flex flex-col justify-center items-start rounded-3xl bg-[#FAF7F2]/[0.04] border border-[#FAF7F2]/10 p-10"
              >
                <p className="font-display text-4xl sm:text-5xl text-[#C5A059]">Thank you.</p>
                <p className="mt-4 text-[#FAF7F2]/75 text-lg max-w-md">
                  Your enquiry has arrived and a confirmation is on its way to your inbox. I'll be in touch very soon to begin your journey.
                </p>
                <button
                  data-testid="booking-reset"
                  onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", area: "", interest: "Package of 11", message: "" }); }}
                  className="mt-8 label text-[#FAF7F2] border-b border-[#C5A059] pb-1"
                >
                  Send another enquiry
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="booking-form" className="rounded-3xl bg-[#FAF7F2]/[0.04] border border-[#FAF7F2]/10 p-7 sm:p-10 grid sm:grid-cols-2 gap-5">
                <Field label="Name" testid="booking-name">
                  <input data-testid="booking-input-name" value={form.name} onChange={update("name")} className={inputCls} placeholder="Your name" />
                </Field>
                <Field label="Email" testid="booking-email">
                  <input data-testid="booking-input-email" type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="you@email.com" />
                </Field>
                <Field label="Phone" testid="booking-phone">
                  <input data-testid="booking-input-phone" value={form.phone} onChange={update("phone")} className={inputCls} placeholder="Optional" />
                </Field>
                <Field label="Your area" testid="booking-area">
                  <input data-testid="booking-input-area" value={form.area} onChange={update("area")} className={inputCls} placeholder="e.g. Hampstead" />
                </Field>
                <Field label="Interested in" testid="booking-interest" full>
                  <select data-testid="booking-input-interest" value={form.interest} onChange={update("interest")} className={`${inputCls} appearance-none`}>
                    <option className="text-black">Package of 11</option>
                    <option className="text-black">Single Session</option>
                    <option className="text-black">Not sure yet</option>
                  </select>
                </Field>
                <Field label="Message" testid="booking-message" full>
                  <textarea data-testid="booking-input-message" value={form.message} onChange={update("message")} rows={4} className={inputCls} placeholder="Tell me about your goals, injuries, or availability…" />
                </Field>
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    data-testid="booking-submit"
                    disabled={sending}
                    className="btn-lux w-full rounded-full bg-[#C5A059] text-[#18181B] px-8 py-4 label hover:bg-[#FAF7F2] disabled:opacity-60"
                  >
                    {sending ? "Sending…" : "Send Enquiry"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full bg-transparent border-b border-[#FAF7F2]/20 focus:border-[#C5A059] outline-none py-3 text-[#FAF7F2] placeholder:text-[#FAF7F2]/35 transition-colors";

function Field({ label, testid, full, children }) {
  return (
    <label data-testid={testid} className={`${full ? "sm:col-span-2" : ""} block`}>
      <span className="label text-[#FAF7F2]/55 text-[0.6rem]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
